import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Card } from '@/shared/components/ui/Card';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useLeaves, useLeaveTypes, useHolidays } from '../hooks/useHr';
import { useAdminUsers } from '../hooks/useHr';
import { DEPT_LABELS } from '../constants';
import type { Leave, Holiday as HolidayType } from '../types';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function toISO(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function isDateInRange(date: string, from: string, to: string): boolean {
  return date >= from && date <= to;
}

export function LeaveCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [deptFilter, setDeptFilter] = useState('all');

  const { data: leaves = [], isLoading: leavesLoading } = useLeaves();
  const { data: leaveTypes = [] } = useLeaveTypes();
  const { data: holidays = [], isLoading: holidaysLoading } = useHolidays(year);
  const { data: users = [] } = useAdminUsers();

  const isLoading = leavesLoading || holidaysLoading;

  // Department options
  const deptOptions = useMemo(() => {
    const depts = new Set(users.map((u) => u.department).filter(Boolean) as string[]);
    return [
      { value: 'all', label: 'All Departments' },
      ...Array.from(depts).map((d) => ({ value: d, label: DEPT_LABELS[d] ?? d })),
    ];
  }, [users]);

  // Approved leaves for the displayed month, filtered by department
  const monthLeaves = useMemo(() => {
    const monthStart = toISO(year, month, 1);
    const monthEnd = toISO(year, month, getDaysInMonth(year, month));
    return leaves.filter((l) => {
      if (l.status !== 'approved') return false;
      // Check overlap with current month
      if (l.to_date < monthStart || l.from_date > monthEnd) return false;
      // Department filter
      if (deptFilter !== 'all' && l.employee_dept !== deptFilter) return false;
      return true;
    });
  }, [leaves, year, month, deptFilter]);

  // Holiday lookup set
  const holidayMap = useMemo(() => {
    const map = new Map<string, HolidayType>();
    for (const h of holidays) map.set(h.date, h);
    return map;
  }, [holidays]);

  // Leave type color lookup
  const typeColorMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const lt of leaveTypes) map.set(lt.code, lt.color);
    return map;
  }, [leaveTypes]);

  // Build calendar grid
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfWeek(year, month);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(year + 1); }
    else setMonth(month + 1);
  };

  // Get leaves for a specific day
  const getLeavesForDay = (day: number): Leave[] => {
    const dateStr = toISO(year, month, day);
    return monthLeaves.filter((l) => isDateInRange(dateStr, l.from_date, l.to_date));
  };

  const todayISO = today.toISOString().slice(0, 10);

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-primary" />
          <h2 className="text-display-sm font-semibold text-on-surface">Team Leave Calendar</h2>
        </div>
        <div className="w-[180px]">
          <Select
            name="cal-dept-filter"
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            options={deptOptions}
          />
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h3 className="text-lg font-semibold text-on-surface">
          {MONTH_NAMES[month]} {year}
        </h3>
        <Button variant="ghost" size="sm" onClick={nextMonth}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : (
        <>
          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-px">
            {WEEKDAYS.map((d) => (
              <div key={d} className="py-2 text-center text-[11px] font-semibold uppercase text-on-surface-variant">
                {d}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-px rounded-card border border-outline bg-outline">
            {/* Empty cells before first day */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[90px] bg-surface-variant/30 p-1" />
            ))}

            {/* Days */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const dateStr = toISO(year, month, day);
              const dayLeaves = getLeavesForDay(day);
              const holiday = holidayMap.get(dateStr);
              const isToday = dateStr === todayISO;
              const isWeekend = new Date(year, month, day).getDay() === 0 || new Date(year, month, day).getDay() === 6;

              return (
                <div
                  key={day}
                  className={`min-h-[90px] p-1 transition-colors ${
                    isToday ? 'bg-primary-container/20' : isWeekend ? 'bg-surface-variant/20' : 'bg-surface'
                  }`}
                >
                  {/* Day number */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-medium ${
                        isToday ? 'bg-primary text-on-primary' : 'text-on-surface'
                      }`}
                    >
                      {day}
                    </span>
                    {holiday && (
                      <span className="text-[9px] font-medium text-danger" title={holiday.name}>
                        H
                      </span>
                    )}
                  </div>

                  {/* Holiday label */}
                  {holiday && (
                    <div className="mt-0.5 truncate text-[9px] text-danger" title={holiday.name}>
                      {holiday.name}
                    </div>
                  )}

                  {/* Leave entries */}
                  <div className="mt-0.5 flex flex-col gap-0.5">
                    {dayLeaves.slice(0, 3).map((l) => {
                      const color = typeColorMap.get(l.type) ?? '#3b82f6';
                      return (
                        <div
                          key={l.id}
                          className="truncate rounded-sm px-1 py-px text-[9px] font-medium text-white"
                          style={{ backgroundColor: color }}
                          title={`${l.employee_name} - ${l.type}${l.is_half_day ? ' (½ day)' : ''}`}
                        >
                          {l.employee_name?.split(' ')[0] ?? '?'}
                          {l.is_half_day ? ' ½' : ''}
                        </div>
                      );
                    })}
                    {dayLeaves.length > 3 && (
                      <span className="text-[9px] text-on-surface-variant">
                        +{dayLeaves.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <Card className="flex flex-wrap items-center gap-3 px-4 py-2">
            <span className="text-[11px] font-medium text-on-surface-variant">Legend:</span>
            {leaveTypes.map((lt) => (
              <div key={lt.code} className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: lt.color }} />
                <span className="text-[11px] text-on-surface">{lt.name}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <span className="inline-flex h-3 w-3 items-center justify-center rounded-sm bg-danger text-[7px] font-bold text-white">H</span>
              <span className="text-[11px] text-on-surface">Holiday</span>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
